import {
    updateOrderShipping,
    type Order,
    type OrderShipping,
} from '@/lib/api/orders/order-api'
import { buildTrackingUrl } from '@mediardex/contracts'
import OrderShippingLabel from './order-shipping-label'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useState } from 'react'
import {
    MdLocalShipping,
    MdOutlineStorefront,
    MdOutlineLocalOffer,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface Props {
    order: Order
    /** Recarrega o pedido depois de gravar — a entrega vem no mesmo GET. */
    onSaved?: () => void
    /** Sem permissão de edição, a seção fica somente leitura. */
    canEdit?: boolean
}

/** Centavos como o resto das telas de atendimento formatam. */
const formatCurrency = (cents: number): string =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

function formatDate(value?: string | null): string {
    if (!value) return '-'
    return format(new Date(value), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
}

/** `yyyy-MM-dd` para o `<input type="date">`, ou vazio. */
function toDateInput(value?: string | null): string {
    if (!value) return ''
    return new Date(value).toISOString().slice(0, 10)
}

/**
 * "Loja e entrega": o que só existe no pedido nascido da vitrine.
 *
 * Aparece apenas quando `origin = 'STORE'` — em atendimento agendado pelo operador não há
 * loja, cupom nem frete a mostrar, e uma seção vazia só ocuparia a tela.
 *
 * O frete é **somente leitura** de propósito: transportadora, serviço, prazo e valor são o
 * snapshot do que o cliente pagou. Editável é o andamento — rastreio, despacho e entrega.
 * Com a etiqueta comprada pelo sistema (`OrderShippingLabel`), o rastreio e a postagem
 * entram sozinhos; sem ela, o operador preenche à mão.
 */
export default function OrderStoreSection({ order, onSaved, canEdit = true }: Props) {
    const shipping: OrderShipping | null | undefined = order.shipping
    const [trackingCode, setTrackingCode] = useState(shipping?.trackingCode || '')
    const [shippedAt, setShippedAt] = useState(toDateInput(shipping?.shippedAt))
    const [deliveredAt, setDeliveredAt] = useState(toDateInput(shipping?.deliveredAt))
    const [saving, setSaving] = useState(false)

    if (order.origin !== 'STORE') return null

    const isPickup = shipping?.kind === 'PICKUP'
    // O que a loja bancou do frete (quem paga: loja, parte, valor fixo ou frete grátis).
    const storeShippingCost =
        shipping && shipping.carrierPrice != null ? shipping.carrierPrice - shipping.price : 0

    const handleSave = async () => {
        setSaving(true)
        try {
            const response = await updateOrderShipping(order.id, {
                trackingCode: trackingCode.trim() || null,
                shippedAt: shippedAt ? new Date(`${shippedAt}T12:00:00`).toISOString() : null,
                deliveredAt: deliveredAt
                    ? new Date(`${deliveredAt}T12:00:00`).toISOString()
                    : null,
            })
            if (response.success) {
                toast.success('Entrega atualizada')
                onSaved?.()
            } else {
                toast.error(response.error || 'Não foi possível atualizar a entrega')
            }
        } finally {
            setSaving(false)
        }
    }

    const deliveryAddress = [
        order.street,
        order.number && `nº ${order.number}`,
        order.complement,
        order.neighborhood,
        order.city && order.state ? `${order.city}/${order.state}` : order.city,
        order.zipCode,
    ]
        .filter(Boolean)
        .join(', ')

    return (
        <div className="bg-base-100 rounded-2xl shadow-sm p-6 mb-6">
            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                <MdOutlineStorefront className="text-xl" />
                Loja e entrega
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-base-200/50 rounded-xl">
                    <span className="text-xs uppercase tracking-wide text-base-content/50">
                        Loja de origem
                    </span>
                    <p className="font-medium text-base-content">
                        {order.storeConfig?.name || 'Loja'}
                    </p>
                    {order.storeConfig?.subdomain && (
                        <p className="text-sm text-base-content/60">
                            {order.storeConfig.subdomain}
                        </p>
                    )}
                </div>

                <div className="p-4 bg-base-200/50 rounded-xl">
                    <span className="text-xs uppercase tracking-wide text-base-content/50">
                        Descontos aplicados
                    </span>
                    {order.promotionDiscount || order.discountAmount ? (
                        <div className="text-sm text-base-content">
                            {!!order.promotionDiscount && (
                                <p className="flex items-center gap-1.5">
                                    <MdOutlineLocalOffer className="text-success" />
                                    Promoção: {formatCurrency(order.promotionDiscount)}
                                </p>
                            )}
                            {!!order.discountAmount && (
                                <p className="flex items-center gap-1.5">
                                    <MdOutlineLocalOffer className="text-success" />
                                    Cupom {order.couponCode}:{' '}
                                    {formatCurrency(order.discountAmount)}
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-base-content/60">Nenhum</p>
                    )}
                </div>
            </div>

            {deliveryAddress && (
                <div className="p-4 bg-base-200/50 rounded-xl mb-6">
                    <span className="text-xs uppercase tracking-wide text-base-content/50">
                        {isPickup ? 'Retirada em' : 'Endereço de entrega'}
                    </span>
                    <p className="text-base-content">{deliveryAddress}</p>
                </div>
            )}

            {!shipping ? (
                <div className="p-4 bg-base-200/50 rounded-xl">
                    <p className="text-sm text-base-content/70">
                        Esta compra não tem frete — combine a retirada com o cliente.
                    </p>
                </div>
            ) : (
                <>
                    <h4 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                        <MdLocalShipping className="text-lg" />
                        {isPickup ? 'Retirada na loja' : 'Frete cobrado'}
                    </h4>
                    {isPickup ? (
                        <div className="p-4 bg-base-200/50 rounded-xl mb-6">
                            <p className="text-sm text-base-content/70">
                                O cliente vem buscar. Marque quando o pedido estiver pronto: ele
                                recebe um aviso por e-mail com o endereço.
                            </p>
                        </div>
                    ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="p-4 bg-base-200/50 rounded-xl">
                            <span className="text-xs uppercase tracking-wide text-base-content/50">
                                Transportadora
                            </span>
                            <p className="font-medium text-base-content">
                                {shipping.carrier} {shipping.service}
                            </p>
                        </div>
                        <div className="p-4 bg-base-200/50 rounded-xl">
                            <span className="text-xs uppercase tracking-wide text-base-content/50">
                                Cliente pagou
                            </span>
                            <p className="font-medium text-base-content">
                                {shipping.freeShipping || shipping.price === 0
                                    ? 'Grátis'
                                    : formatCurrency(shipping.price)}
                            </p>
                            {storeShippingCost > 0 && (
                                <p className="text-xs text-base-content/60 mt-0.5">
                                    A loja paga {formatCurrency(storeShippingCost)} (transportadora cobra{' '}
                                    {formatCurrency(shipping.carrierPrice ?? 0)})
                                </p>
                            )}
                        </div>
                        <div className="p-4 bg-base-200/50 rounded-xl">
                            <span className="text-xs uppercase tracking-wide text-base-content/50">
                                Prazo estimado
                            </span>
                            <p className="font-medium text-base-content">
                                {shipping.deliveryDays
                                    ? `${shipping.deliveryDays} dias úteis`
                                    : '-'}
                            </p>
                        </div>
                    </div>
                    )}

                    {!isPickup && (
                        <OrderShippingLabel order={order} canEdit={canEdit} onChanged={onSaved} />
                    )}

                    {canEdit ? (
                        <div className="border-t border-base-200 pt-5">
                            <h4 className="font-semibold text-base-content mb-4">
                                {isPickup ? 'Andamento da retirada' : 'Andamento da entrega'}
                            </h4>
                            <div className={`grid grid-cols-1 gap-4 ${isPickup ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
                                {!isPickup && (
                                <div className="form-control">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Código de rastreio
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: AA123456789BR"
                                        value={trackingCode}
                                        onChange={e => setTrackingCode(e.target.value)}
                                    />
                                    {buildTrackingUrl(trackingCode) && (
                                        <label className="label pt-1">
                                            <a
                                                href={buildTrackingUrl(trackingCode)!}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="label-text-alt link link-primary"
                                            >
                                                Abrir rastreio
                                            </a>
                                        </label>
                                    )}
                                </div>
                                )}
                                <div className="form-control">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            {isPickup ? 'Pronto para retirada em' : 'Despachado em'}
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={shippedAt}
                                        onChange={e => setShippedAt(e.target.value)}
                                    />
                                    {!shipping?.shippedAt && (
                                        <label className="label pt-1">
                                            <span className="label-text-alt text-base-content/50">
                                                {isPickup
                                                    ? 'Ao salvar, o cliente recebe um e-mail de que pode retirar.'
                                                    : 'Ao salvar, o cliente recebe um e-mail de pedido enviado.'}
                                            </span>
                                        </label>
                                    )}
                                </div>
                                <div className="form-control">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            {isPickup ? 'Retirado em' : 'Entregue em'}
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={deliveredAt}
                                        onChange={e => setDeliveredAt(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end mt-4">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Salvando...' : isPickup ? 'Salvar retirada' : 'Salvar entrega'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="border-t border-base-200 pt-5 text-sm text-base-content/70 space-y-1">
                            <p>
                                Rastreio: {shipping.trackingCode || '-'}
                                {buildTrackingUrl(shipping.trackingCode) && (
                                    <a
                                        href={buildTrackingUrl(shipping.trackingCode)!}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link link-primary ml-2"
                                    >
                                        Acompanhar
                                    </a>
                                )}
                            </p>
                            <p>
                                {isPickup ? 'Pronto para retirada em' : 'Despachado em'}:{' '}
                                {formatDate(shipping.shippedAt)}
                            </p>
                            <p>
                                {isPickup ? 'Retirado em' : 'Entregue em'}: {formatDate(shipping.deliveredAt)}
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
